import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Sparkles, Building2, Hotel, CheckCircle2, Shield, Clock, Leaf, Award, DollarSign, Zap, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import AthensBanner from './AthensBanner';
import Navigation from './Navigation';
import ContactBar from './ContactBar';
import StatsSection from './StatsSection';
import VideoSection from './VideoSection';
import PhotoGallery from './PhotoGallery';
import CTASection from './CTASection';
import BeforeAfterSection from './BeforeAfterSection';
import BookingForm from './BookingForm';
import ContactFooter from './ContactFooter';
import { images } from '../mockData';

const HomePage = () => {
  const { t } = useLanguage();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i} className={i < rating ? 'text-yellow-400' : 'text-gray-300'}>★</span>
      );
    }
    return stars;
  };

  return (
    <div className="min-h-screen bg-white">
      <AthensBanner />
      <Navigation scrollToSection={scrollToSection} />
      <ContactBar />

      {/* Hero Section */}
      <section id="home" className="hero-section relative">
        <div className="container max-w-5xl relative z-10">
          <div className="animate-fadeInUp">
            <h1 className="heading-1 mb-6">{t.hero.title}</h1>
          </div>
          <div className="animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
            <p className="body-large mb-8 max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
              {t.hero.subtitle}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
            <Button 
              onClick={() => scrollToSection('booking')} 
              className="btn-primary"
            >
              {t.hero.cta}
            </Button>
            <a 
              href="tel:+306994411844" 
              className="btn-secondary flex items-center gap-2"
            >
              <Phone className="w-4 h-4" />
              {t.hero.phone}
            </a>
          </div>
          
          {/* Hero Image */}
          <div className="mt-12 animate-scaleIn" style={{ animationDelay: '0.6s' }}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl max-w-4xl mx-auto">
              <img 
                src={images.hero}
                alt="Clean modern home"
                className="w-full h-auto"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <StatsSection />

      {/* About Section */}
      <section id="about" className="pad-2xl" style={{ background: 'var(--bg-section)' }}>

      {/* Video Section */}
      <VideoSection />

      {/* CTA Section */}
      <CTASection />

      {/* Services Section */}
        <div className="container">
          <div className="animate-fadeInUp">
            <h2 className="heading-2 text-center mb-6">{t.about.title}</h2>
            <p className="body-medium text-center max-w-3xl mx-auto mb-8">
              {t.about.description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mt-12">
            <div className="animate-slideInLeft">
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={images.employee}
                  alt="Professional cleaning service"
                  className="w-full h-auto"
                />
              </div>
            </div>
            
            <div className="animate-slideInRight">
              <h3 className="heading-3 mb-6">{t.about.why}</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span className="body-medium">{t.about.points[0]}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span className="body-medium">{t.about.points[1]}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span className="body-medium">{t.about.points[2]}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span className="body-medium">{t.about.points[3]}</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: 'var(--accent-primary)' }} />
                  <span className="body-medium">{t.about.points[4]}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="pad-2xl">
        <div className="container">
          <h2 className="heading-2 text-center mb-4">{t.services.title}</h2>
          <p className="body-medium text-center mb-12" style={{ color: 'var(--text-secondary)' }}>
            {t.services.subtitle}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="product-card">
              <CardHeader>
                <div className="mb-4" style={{ color: 'var(--accent-text)' }}>
                  <Sparkles className="w-8 h-8" />
                </div>
                <CardTitle className="heading-3">{t.services.items[0].title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="body-small">
                  {t.services.items[0].description}
                </CardDescription>
              </CardContent>
            </Card>
            <Card className="product-card">
              <CardHeader>
                <div className="mb-4" style={{ color: 'var(--accent-text)' }}>
                  <Hotel className="w-8 h-8" />
                </div>
                <CardTitle className="heading-3">{t.services.items[1].title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="body-small">
                  {t.services.items[1].description}
                </CardDescription>
              </CardContent>
            </Card>
            <Card className="product-card">
              <CardHeader>
                <div className="mb-4" style={{ color: 'var(--accent-text)' }}>
                  <Building2 className="w-8 h-8" />
                </div>
                <CardTitle className="heading-3">{t.services.items[2].title}</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="body-small">
                  {t.services.items[2].description}
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Before/After Section */}
      <BeforeAfterSection />

      {/* Photo Gallery */}
      <PhotoGallery />

      <BookingForm />

      {/* Reviews Section */}
      <section id="reviews" className="pad-2xl">
        <div className="container">
          <h2 className="heading-2 text-center mb-4">{t.reviews.title}</h2>
          <p className="body-medium text-center mb-12" style={{ color: 'var(--text-secondary)' }}>
            {t.reviews.subtitle}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="product-card">
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <CardTitle className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {t.reviews.items[0].name}
                  </CardTitle>
                  <div className="flex text-lg">
                    {renderStars(t.reviews.items[0].rating)}
                  </div>
                </div>
                <p className="caption" style={{ color: 'var(--text-muted)' }}>
                  {t.reviews.items[0].service}
                </p>
              </CardHeader>
              <CardContent>
                <p className="body-small">{t.reviews.items[0].text}</p>
              </CardContent>
            </Card>

            <Card className="product-card">
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <CardTitle className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {t.reviews.items[1].name}
                  </CardTitle>
                  <div className="flex text-lg">
                    {renderStars(t.reviews.items[1].rating)}
                  </div>
                </div>
                <p className="caption" style={{ color: 'var(--text-muted)' }}>
                  {t.reviews.items[1].service}
                </p>
              </CardHeader>
              <CardContent>
                <p className="body-small">{t.reviews.items[1].text}</p>
              </CardContent>
            </Card>

            <Card className="product-card">
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <CardTitle className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {t.reviews.items[2].name}
                  </CardTitle>
                  <div className="flex text-lg">
                    {renderStars(t.reviews.items[2].rating)}
                  </div>
                </div>
                <p className="caption" style={{ color: 'var(--text-muted)' }}>
                  {t.reviews.items[2].service}
                </p>
              </CardHeader>
              <CardContent>
                <p className="body-small">{t.reviews.items[2].text}</p>
              </CardContent>
            </Card>

            <Card className="product-card">
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <CardTitle className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {t.reviews.items[3].name}
                  </CardTitle>
                  <div className="flex text-lg">
                    {renderStars(t.reviews.items[3].rating)}
                  </div>
                </div>
                <p className="caption" style={{ color: 'var(--text-muted)' }}>
                  {t.reviews.items[3].service}
                </p>
              </CardHeader>
              <CardContent>
                <p className="body-small">{t.reviews.items[3].text}</p>
              </CardContent>
            </Card>

            <Card className="product-card">
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <CardTitle className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {t.reviews.items[4].name}
                  </CardTitle>
                  <div className="flex text-lg">
                    {renderStars(t.reviews.items[4].rating)}
                  </div>
                </div>
                <p className="caption" style={{ color: 'var(--text-muted)' }}>
                  {t.reviews.items[4].service}
                </p>
              </CardHeader>
              <CardContent>
                <p className="body-small">{t.reviews.items[4].text}</p>
              </CardContent>
            </Card>

            <Card className="product-card">
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <CardTitle className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {t.reviews.items[5].name}
                  </CardTitle>
                  <div className="flex text-lg">
                    {renderStars(t.reviews.items[5].rating)}
                  </div>
                </div>
                <p className="caption" style={{ color: 'var(--text-muted)' }}>
                  {t.reviews.items[5].service}
                </p>
              </CardHeader>
              <CardContent>
                <p className="body-small">{t.reviews.items[5].text}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="why-choose" className="pad-2xl" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <h2 className="heading-2 text-center mb-12">{t.whyChoose.title}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0" style={{ color: 'var(--accent-text)' }}>
                <Shield className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2" style={{ color: 'var(--text-primary)' }}>
                  {t.whyChoose.points[0].title}
                </h3>
                <p className="body-small">{t.whyChoose.points[0].description}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0" style={{ color: 'var(--accent-text)' }}>
                <Clock className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2" style={{ color: 'var(--text-primary)' }}>
                  {t.whyChoose.points[1].title}
                </h3>
                <p className="body-small">{t.whyChoose.points[1].description}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0" style={{ color: 'var(--accent-text)' }}>
                <Leaf className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2" style={{ color: 'var(--text-primary)' }}>
                  {t.whyChoose.points[2].title}
                </h3>
                <p className="body-small">{t.whyChoose.points[2].description}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0" style={{ color: 'var(--accent-text)' }}>
                <Award className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2" style={{ color: 'var(--text-primary)' }}>
                  {t.whyChoose.points[3].title}
                </h3>
                <p className="body-small">{t.whyChoose.points[3].description}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0" style={{ color: 'var(--accent-text)' }}>
                <DollarSign className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2" style={{ color: 'var(--text-primary)' }}>
                  {t.whyChoose.points[4].title}
                </h3>
                <p className="body-small">{t.whyChoose.points[4].description}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0" style={{ color: 'var(--accent-text)' }}>
                <Zap className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2" style={{ color: 'var(--text-primary)' }}>
                  {t.whyChoose.points[5].title}
                </h3>
                <p className="body-small">{t.whyChoose.points[5].description}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactFooter />
    </div>
  );
};

export default HomePage;
